import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FieldComponentType } from './enums';

import { IField, ISchema, InputFieldType } from './interfaces';
import { AdvancedSearchLibService } from './advanced-search-lib.service';
import { FieldAttributeTranslationValue } from '../enums';

@Injectable({
    providedIn: 'root'
})
export class UtilsService {
    constructor(private advSearchLibSvc: AdvancedSearchLibService) {
    }

    getFieldAttributeTranslation(
        field: IField,
        translationKey: FieldAttributeTranslationValue | string,
        language: string,
        defaultText?: string
    ): string {
        if (!(field?.attributeTranslations)) {
            return defaultText || '';
        }
        const attrTranslation = field.attributeTranslations.find(x => x.value === translationKey);
        if (!attrTranslation) {
            return defaultText || '';
        }
        const langTranslation = attrTranslation.trans[language];
        return langTranslation || defaultText || '';
    }

    setTravelDateFieldsTimezone(fields: IField[]): void {
        for (const field of fields) {
            if (field.type === 'travel-date') {
                field.timezone = 'UTC';
            }
        }
    }

    clone<T>(obj: T): T {
        return JSON.parse(JSON.stringify(obj));
    }

    getDebouncedDistinctObservable<T>(source: Observable<T>, interval: number): Observable<T> {
        return source.pipe(
            debounceTime(interval),
            distinctUntilChanged()
        );
    }

    escapeHtml(text: string): string {
        if (!text) {
            return text;
        }
        return text.replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }

    calculateTimeLeft(targetDate: Date): { days: number, hours: number, minutes: number, seconds: number } {
        const now = Date.now();
        let diffInseconds = Math.round((targetDate.getTime() - now) / 1000);
        if (diffInseconds <= 0) {
            return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }
        const days = Math.floor(diffInseconds / 86400);
        diffInseconds -= days * 86400;
        const hours = Math.floor(diffInseconds / 3600) % 24;
        diffInseconds -= hours * 3600;
        const minutes = Math.floor(diffInseconds / 60) % 60;
        diffInseconds -= minutes * 60;
        const seconds = diffInseconds;
        return { days: days, hours: hours, minutes: minutes, seconds: seconds };
    }

    getNonNullSchemaType(schemaType: string | string[]): string {
        if (typeof schemaType === 'string') {
            return schemaType;
        }

        if (Array.isArray(schemaType)) {
            const typeArr = [...schemaType as string[]];
            // Remove 'null' item from the array
            typeArr.splice(typeArr.indexOf('null'));
            // And return the first from the rest
            return typeArr[0];
        }

        return schemaType;
    }

    // getFieldTypeFromField(field: IField): InputFieldType {
    //     // TODO: Use @azavista/advanced-search ?
    //     if (field.schema.format === 'date' || field.schema.format === 'date-time' || field.schema.format === 'time') {
    //         return field.schema.format as InputFieldType;
    //     } else if (this.getNonNullSchemaType(field.schema.type) === 'string' && field.schema.enum) {
    //         return 'enum';
    //     } else if (field.type === 'document' || field.type === 'image') {
    //         return field.type;
    //     }
    //     return this.getNonNullSchemaType(field.schema.type) as InputFieldType;
    // }
    getFieldTypeFromField(field: IField): InputFieldType {
        // Fields without component value or if it is default or timezone are not virtual
        // Timezone will be treated either as select or virtual-autocomplete inside the input field
        if (field.component && field.component !== FieldComponentType.default && field.component !== FieldComponentType.timezone) {
            return ('virtual-' + field.component) as InputFieldType;
        }

        const libFieldType = this.advSearchLibSvc.getFieldTypeFromField(field);
        return libFieldType as InputFieldType;
    }

    // getFieldTypeFromSchema(schema: ISchema, componentType: string): InputFieldType {
    //     if (componentType && componentType !== 'default') {
    //         // If component type is provided, we assume this is a custom field type
    //         return 'custom';
    //     }
    //     // ajv supports the following types:
    //     // number, integer, string, boolean, array, object or null
    //     // and following formats:
    //     // date, date-time, uri, email, hostname, ipv4, ipv6, regex
    //     if (schema.format === 'date' || schema.format === 'date-time' || schema.format === 'time') {
    //         return schema.format as InputFieldType;
    //     } else if (this.getNonNullSchemaType(schema.type) === 'string' && schema.enum) {
    //         return 'enum';
    //     } else if (schema.format === 'email') {
    //         return 'email';
    //     }

    //     const nonNullSchemaType = this.getNonNullSchemaType(schema.type);
    //     return nonNullSchemaType as InputFieldType;
    // }

    getInputElementValue(field: IField, inputElement: HTMLElement): any {
        const inpEl = inputElement as HTMLInputElement;
        const type = inpEl.type;
        if (type === 'number') {
            return inpEl.valueAsNumber;
        } else if (type === 'checkbox') {
            return inpEl.checked;
        } else if (type === 'select-multiple') {
            const multiSelectEl = inputElement as HTMLSelectElement;
            const arrValue: string[] = [];
            for (let i = 0; i < multiSelectEl.selectedOptions.length; i++) {
                arrValue.push(multiSelectEl.selectedOptions.item(i).value);
            }
            return arrValue;
        } else if (type === 'time'
            // Check that it is not in date-time input field
            && !inpEl.hasAttribute('time-input')) {
            return inpEl.value && `${inpEl.value}:00`;
        }
        // For Backward support of date-time and time input sections that used select
        // If field is of type date-time, we must collect values from 3 fields
        const fieldType = this.getFieldTypeFromField(field);
        if (fieldType === 'datetime' || fieldType === 'travel-date') {
            // Get parent element so we can select the 3 elements of the date-time
            const parentElement = inputElement.parentElement;
            const dateInpEl = parentElement.querySelector('[date-time-input]') as HTMLInputElement;
            if (!dateInpEl || !dateInpEl.value) {
                // In case it is the new date-time field
                const dateEl = parentElement.querySelector('[date-input]') as HTMLInputElement;
                const timeInpEl = parentElement.querySelector('[time-input]') as HTMLInputElement;

                if (dateEl && dateEl.value && timeInpEl && timeInpEl.value) {
                    // When user selects time, the time input value contains only hours and minutes
                    // We need to add seconds to make the date valid. If seconds are provided, the value was set by the code
                    // because the data that comes from the server contains them
                    const timeValue = timeInpEl.value.length === 5 ? timeInpEl.value + ':00' : timeInpEl.value;
                    return `${dateEl.value}T${timeValue}Z`;
                }

                return null;
            }
            const hourSelectEl = parentElement.querySelector('[date-time-hours-select]') as HTMLSelectElement;
            const minutesSelectEl = parentElement.querySelector('[date-time-minutes-select]') as HTMLSelectElement;

            const hourValue = this.prefixWithZero(+hourSelectEl.value);
            const minuteValue = this.prefixWithZero(+minutesSelectEl.value);
            const dateTime = dateInpEl.value + `T${hourValue}:${minuteValue}:00Z`;
            return dateTime;
        } else if (fieldType === 'time') {
            // Get parent element so we can select the 2 elements of hours and minute
            const parentElement = inputElement.parentElement;
            const hourSelectEl = parentElement.querySelector('[time-hours-select]') as HTMLSelectElement;
            const minutesSelectEl = parentElement.querySelector('[time-minutes-select]') as HTMLSelectElement;

            const hourValue = this.prefixWithZero(+hourSelectEl.value);
            const minuteValue = this.prefixWithZero(+minutesSelectEl.value);
            const dateTime = `${hourValue}:${minuteValue}:00Z`;
            return dateTime;
        }
        return inpEl.value;
    }

    prefixWithZero(value: number): string {
        if (value < 10) {
            return '0' + value;
        }
        return '' + value;
    }
}

export const delay = async (ms: number) => {
    return new Promise<void>(resolve => {
        setTimeout(() => resolve(), ms);
    });
};

export const getMapByGetter = <
    T extends { [key in string]: any },
    G extends ((item: T) => any),
>(
    rows: T[],
    getterFunction: G ,
): { [key in ReturnType<G> ]?: T } => {
    return rows.reduce(
        (group, row) => {
            group[getterFunction(row)] = row;
            return group;
        },
        {} as { [key in  ReturnType<G>]?: T },
    );
};

